import { isValidCEP } from '../../../../utils/validation';
import type { ShippingAddressData } from '../../../../types';
import type { CepApiResponse, RepublicaVirtualResponse } from '../types/shipping-address.types';

export async function fetchAddressByZipCode(cep: string): Promise<Partial<ShippingAddressData> | undefined> {
  if (!cep || cep.length < 8) {
    return undefined;
  }

  const cleanedCep = cep.replace(/\D/g, '');
  if (!isValidCEP(cleanedCep)) {
    return undefined;
  }

  // Try ViaCEP first
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanedCep}/json/`);
    if (response.ok) {
      const addressData: CepApiResponse = await response.json();
      if (!addressData.erro) {
        return {
          street: addressData.logradouro ?? '',
          neighborhood: addressData.bairro ?? '',
          city: addressData.localidade ?? '',
          state: addressData.uf ?? '',
          zipCode: cleanedCep,
        };
      }
    }
  } catch (error) {
    console.error('Failed to fetch CEP from viacep', error);
  }

  // Fallback to BrasilAPI
  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${cleanedCep}`);
    if (response.ok) {
      const data = await response.json();
      if (data?.cep) {
        return {
          street: data.street ?? '',
          neighborhood: data.neighborhood ?? '',
          city: data.city ?? '',
          state: data.state ?? '',
          zipCode: cleanedCep,
        };
      }
    }
  } catch (error) {
    console.error('Failed to fetch CEP from brasilapi', error);
  }

  // Fallback to OpenCEP
  try {
    const response = await fetch(`https://opencep.com/v1/${cleanedCep}`);
    if (response.ok) {
      const data = await response.json();
      if (data?.cep) {
        return {
          street: data.logradouro ?? '',
          neighborhood: data.bairro ?? '',
          city: data.localidade ?? '',
          state: data.uf ?? '',
          zipCode: cleanedCep,
        };
      }
    }
  } catch (error) {
    console.error('Failed to fetch CEP from opencep', error);
  }

  // Fallback to RepublicaVirtual
  try {
    const response = await fetch(`http://cep.republicavirtual.com.br/web_cep.php?formato=json&cep=${cleanedCep}`);
    if (response.ok) {
      const resp: RepublicaVirtualResponse = await response.json();
      // resultado "0" = error, "1" = success, "2" = not found with suggestion
      if (resp.resultado === '1' || resp.resultado === '2') {
        return {
          street: `${resp.tipo_logradouro || ''} ${resp.logradouro || ''}`.trim(),
          neighborhood: resp.bairro ?? '',
          city: resp.cidade ?? '',
          state: resp.uf ?? '',
          zipCode: cleanedCep,
        };
      }
    }
  } catch (error) {
    console.error('Failed to fetch CEP from republicavirtual', error);
  }

  return undefined;
} 